<template>
  <v-dialog
    v-model="bulkUploadDialog"
    persistent
    scrollable
    width="90%"
    max-width="60vw"
  >
    <v-card>
      <v-card-title class="subHeading py-2">
        <span class="text-capitalize"
          >Import
          {{
            uploadTo == "customer_address"
              ? "Customer"
              : uploadTo.replace(/_/g, " ")
          }}</span
        >
        <v-spacer></v-spacer>
        <v-btn color="subHeading" depressed fab small @click="closeDialog()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row v-if="nonFieldErrors.length > 0">
          <v-col cols="12">
            <v-alert color="error" class="white--text" dismissible dense>
              <p v-for="(error, i) in nonFieldErrors" :key="i">
                {{ error }}
              </p>
              <template #close>
                <v-icon @click="nonFieldErrors = []" color="white"
                  >mdi-close</v-icon
                >
              </template>
            </v-alert>
          </v-col>
        </v-row>
        <v-row v-if="fileRowsLimitExceeds">
          <v-col cols="12">
            <v-alert
              v-model="fileRowsLimitExceeds"
              color="error"
              class="white--text"
              text
              dismissible
              dense
            >
              <span
                >The file you are trying to upload contains more than 2000 rows,
                which exceeds the maximum limit allowed. Please split the file
                into smaller chunks and try again.</span
              >
            </v-alert>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col
            cols="12"
            class="d-flex justify-end pb-2"
            v-if="hasBulkActions"
          >
            <v-btn-toggle
              v-model="bulkAction"
              borderless
              mandatory
              active-class="primary white--text"
            >
              <v-btn value="Upload" large>
                <span class="hidden-sm-and-down">Create</span>
                <v-icon right :color="bulkAction == 'Upload' ? 'white' : ''">
                  mdi-table-plus
                </v-icon>
              </v-btn>
              <v-btn value="Update" large>
                <span class="hidden-sm-and-down">Update</span>
                <v-icon right :color="bulkAction == 'Update' ? 'white' : ''">
                  mdi-table-edit
                </v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" v-show="rowData && rowData.length == 0">
            <div class="app-relative rounded-lg d-flex justify-center drop-box">
              <FileUpload
                @selectedFile="getFileData"
                height="100%"
                accept=".xlsx, .xls, .csv"
                :multiple="false"
                ref="fileUpload"
              />
              <div style="position: absolute !important; left: 10px; top: 10px">
                <v-icon
                  color="accent"
                  class="mr-2"
                  @click="openInstructionDialog"
                  >mdi-information</v-icon
                >
                <span class="text-caption" v-if="rowData && rowData.length > 0">
                  Records with Error : {{ rowData.length }}</span
                >
                <span class="text-caption" v-if="totalRecordCount > 0">
                  Total Records : {{ totalRecordCount }}</span
                >
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row no-gutters v-if="rowData.length > 0">
          <v-col cols="12">
            <AgGridVue
              @grid-ready="gridReady"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :default-col-def="defaultColDef"
              :context="context"
              :row-data="rowData"
              style="width: 100%; height: 400px"
              class="ag-theme-balham"
            >
            </AgGridVue>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-8 py-4 d-flex justify-space-between">
        <div class="d-flex">
          <SecondaryButton
            @click="downloadSampleExcel()"
            name="Sample File"
            iconClass="secondary--text mr-2"
            icon="mdi-download"
            btnClass="mr-2 rounded-lg"
            outlined
          >
          </SecondaryButton>
          <div v-if="distinctErrors.length > 0">
            <SecondaryButton
              class="error"
              @click="
                removeAllRecordWithError();
                $refs.fileUpload.removeFile();
              "
              name="Remove All With Error"
            >
            </SecondaryButton>
          </div>
        </div>
        <div>
          <SecondaryButton
            @click="
              removeAllRecordWithError();
              $refs.fileUpload.removeFile();
            "
            text
            btnClass="mr-2 rounded-lg"
            name="Reset"
          >
          </SecondaryButton>
          <PrimaryButton
            :disabled="!file || rowData.length != 0"
            :loading="loading"
            @click="submitData()"
            name="Submit"
          >
          </PrimaryButton>
        </div>
      </v-card-actions>
    </v-card>
    <Instruction
      v-model="instructionDialog"
      :instructionList="instructionList"
    />
  </v-dialog>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import XLSX from "xlsx";
import { AgGridVue } from "ag-grid-vue";
import Instruction from "@/components/BaseComponents/Instruction.vue";
import { convertToBlobAndDownload, downloadBlob } from "@/utils/functions.js";
import BulkUploadErrorTooltip from "@/components/Button/BulkUploadErrorTooltip.vue";
import RemoveRowButtonBulkUpload from "@/components/Button/RemoveRowButtonBulkUpload.vue";
import { bus } from "@/main";

export default {
  components: {
    AgGridVue,
    Instruction,
    BulkUploadErrorTooltip,
    RemoveRowButtonBulkUpload,
  },
  props: {
    value: Boolean,
    uploadTo: {
      required: true,
      type: [String, null],
    },
    hasBulkActions: { type: Boolean, default: false },
  },
  data() {
    return {
      bulkAction: "Upload",
      loading: false,
      file: null,
      fulldata: [],
      rowData: [],
      columnDefs: [],
      _reqFields: [],
      _allFields: null,
      gridApi: null,
      columnApi: null,
      fileRowsLimitExceeds: false,
      gridOptions: {
        headerHeight: 40,
        rowHeight: 40,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
      },
      defaultColDef: {
        lockPosition: true,
      },
      requiredHeaders: [],
      serverErrors: [],

      // For Instructions Dialog
      instructionList: [],
      instructionDialog: false,
      dataObject: [],

      nonFieldErrors: [],
    };
  },
  watch: {
    totalRecordCount(val) {
      if (val == 0) {
        this.$refs.fileUpload.removeFile();
      }
      if (val > 11000) {
        this.fileRowsLimitExceeds = true;
        this.$refs.fileUpload.removeFile();
      }
    },
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    totalRecordCount() {
      return this.dataObject.filter((item) => Object.keys(item).length > 0)
        .length;
    },
    bulkUploadDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    distinctErrors() {
      let err = [];

      this.serverErrors
        .filter((e) => {
          return Object.keys(e).length > 0;
        })
        .map((m) => {
          return Object.keys(m).map((k) => {
            return m[k];
          });
        })
        .forEach((r) => {
          r.forEach((s) => {
            err = [...new Set([...err, ...s])];
          });
        });
      return err;
    },
  },
  methods: {
    openInstructionDialog() {
      if (this.uploadTo == "driver") {
        this.instructionList = [
          "Username",
          "First Name",
          "Last Name",
          "Contact Number",
          "Password",
          "Status",
          "Shift Start",
          "Shift End",
          "Vehicle Assigned",
          "Project",
          "Is Active",
        ];
      } else if (this.uploadTo == "vehicle") {
        this.instructionList = [
          "Vehicle Number",
          "Tonnage Capacity",
          "CBM Capacity",
          "Fuel Type",
          "Vehicle Make",
          "Vehicle Model",
          "Vehicle Year",
          "Vehicle Cost",
          "Odometer Reading",
          "Branch Code",
          "Storage Type",
          "Is Active",
          "Deactivation Remarks",
        ];
      } else if (this.uploadTo == "customer_address") {
        this.instructionList = [
          "Customer Code",
          "Customer Name",
          "Branch",
          "Customer Contact Number",
          "Customer Contact Email",
          "Email Notification",
          "Whatsapp Notification",
          "Location Id",
          "Contact Person",
          "Contact Number",
          "Contact Email",
          "Address Info",
          "Address",
          "Latitude",
          "Longitude",
          "From Time",
          "To Time",
          "Processing Time",
          "Remarks",
        ];
      } else if (this.uploadTo == "item") {
        this.instructionList = [
          "ITEM NO",
          "Storage Type",
          "Weight",
          "Volume",
          "Case",
          "Factor",
          "Length",
          "Width",
          "Height",
          "Description",
        ];
      } else if (this.uploadTo == "sale_order") {
        this.instructionList = [
          "Branch Code",
          "Order Type",
          "Reference Number",
          "Order Date",
          "Location ID",
          "Warehouse",
          "Payment Method",
          "Order Value",
          "Is POD Required",
          "Item No",
          "Item Warehouse",
          "Quantity",
          "Order Remarks",
        ];
      } else if (this.uploadTo == "adhoc_order") {
        this.instructionList = [
          "Branch Code",
          "Order Type",
          "Reference Number",
          "Order Date",
          "Pickup Address",
          "Pickup Address Latitude",
          "Pickup Address Longitude",
          "Drop Address",
          "Drop Address Latitude",
          "Drop Address Longitude",
          "Order Remarks",
          "Payment Method",
          "Order Value",
          "Is POD Required",
          "Customer Name",
          "Contact Number",
          "Contact Email",
          "From Time",
          "To Time",
          "Processing Time",
          "Item No",
          "Quantity",
        ];
      } else if (this.uploadTo == "customer_tag") {
        this.instructionList = ["Tag", "Location Id"];
      } else if (this.uploadTo == "vehicle_tag") {
        this.instructionList = ["Tag", "Vehicle Number"];
      } else if (this.uploadTo == "user_management") {
        this.instructionList = [
          "Username",
          "First Name",
          "Last Name",
          "Email",
          "Contact Number",
          "Password",
          "Branch Code",
        ];
      }
      this.instructionDialog = true;
    },
    gridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },
    getFileData(file) {
      if (file) {
        this.file = file;
        let reader = new FileReader();
        reader.onload = async () => {
          this.excelData = [];
          let fileData = reader.result;
          let wb = XLSX.read(fileData, {
            type: "binary",
            cellDates: true,
          });
          let rowData = XLSX.utils.sheet_to_row_object_array(
            wb.Sheets[wb.SheetNames[0]],
            {
              header: 0,
              defval: "",
              raw: false,
              cellNF: false,
            }
          );
          this.dataObject = rowData.map((row, index) => {
            let newObject = {};
            newObject.oldIndex = index;
            for (const [key, value] of Object.entries(row)) {
              let newKey = key
                .toString()
                .toLowerCase()
                .trim()
                .replace(/ /g, "_");
              newObject[newKey] = value.trim();
            }
            return newObject;
          });
        };

        reader.readAsBinaryString(file);
      } else {
        this.clearDialogData();
      }
    },
    removeErrorRow(index, oldIndex) {
      setTimeout(() => {
        this.serverErrors.splice(index, 1);
        this.rowData.splice(index, 1);

        this.dataObject.splice(
          this.dataObject.findIndex((item) => item.oldIndex == oldIndex),
          1
        );
      }, 200);
    },
    dataChange(param) {
      let field = param.colDef.field;
      let localError = this.serverErrors[param.node.rowIndex];
      if (param && param.newValue != param.oldValue) {
        delete localError[field];
      }
      let newIndex = this.dataObject.findIndex(
        (item) => item.oldIndex == param.data.oldIndex
      );
      this.dataObject[newIndex] = param.data;

      if (
        localError.non_field_errors ||
        (Object.keys(localError) && Object.keys(localError).length == 0)
      ) {
        setTimeout(() => {
          this.serverErrors.splice(param.node.rowIndex, 1);
          this.rowData.splice(param.node.rowIndex, 1);
        }, 200);
      }

      return param.newValue;
    },
    cellStyle(param) {
      if (
        param &&
        param.colDef &&
        param.colDef.field &&
        this.serverErrors[param.rowIndex].hasOwnProperty(param.colDef.field)
      ) {
        return {
          backgroundColor: "red",
        };
      } else {
        return {
          backgroundColor: "white",
        };
      }
    },
    removeAllRecordWithError() {
      while (this.rowData.length > 0) {
        this.removeDataFromRow(this.rowData[0].oldIndex, 0);
      }
    },
    removeDataFromRow(oldIndex, currentIndex) {
      this.dataObject.splice(oldIndex, 1, {});
      this.rowData.splice(currentIndex, 1);

      let data = this.dataObject.filter((obj) => Object.keys(obj).length !== 0);

      if (data.length == 0) {
        this.clearDialogData();
      }

      if (this.rowData.length == 0) {
        this.serverErrors = [];
      }
    },
    returnDownloadFunction(requestType) {
      if (requestType == "download") {
        return this.uploadTo == "driver"
          ? this.bulkAction == "Upload"
            ? "getDriverSampleSheet"
            : "getDriverUpdateSampleSheet"
          : this.uploadTo == "vehicle"
          ? this.bulkAction == "Upload"
            ? "getVehicleSampleSheet"
            : "getVehicleUpdateSampleSheet"
          : this.uploadTo == "customer_address"
          ? this.bulkAction == "Upload"
            ? "getCustomerAddressSampleSheet"
            : "getCustomerAddressUpdateSampleSheet"
          : this.uploadTo == "sale_order"
          ? "getSaleOrderSampleSheet"
          : this.uploadTo == "customer_tag"
          ? "getCustomerTagSampleSheet"
          : this.uploadTo == "vehicle_tag"
          ? "getVehicleTagSampleSheet"
          : this.uploadTo == "adhoc_order"
          ? "getAdhocOrderSampleSheet"
          : this.uploadTo == "user_management"
          ? "getUserManagementSampleSheet"
          : "getItemSampleSheet";
      } else {
        return this.uploadTo == "driver"
          ? `bulk${this.bulkAction}Driver`
          : this.uploadTo == "vehicle"
          ? `bulk${this.bulkAction}Vehicle`
          : this.uploadTo == "customer_address"
          ? `bulk${this.bulkAction}CustomerAddress`
          : this.uploadTo == "sale_order"
          ? "bulkUploadSaleOrder"
          : this.uploadTo == "customer_tag"
          ? "bulkUploadCustomerTag"
          : this.uploadTo == "vehicle_tag"
          ? "bulkUploadVehicleTag"
          : this.uploadTo == "adhoc_order"
          ? "bulkUploadAdhocOrder"
          : this.uploadTo == "user_management"
          ? "bulkUploadUserManagement"
          : `bulk${this.bulkAction}Item`;
      }
    },
    downloadSampleExcel() {
      this.$api.bulkUpload[this.returnDownloadFunction("download")]()
        .then((res) => {
          convertToBlobAndDownload(
            res.data,
            `Sample ${
              this.uploadTo != "customer_address"
                ? this.uploadTo.replace(/_/g, " ")
                : "Customer"
            } excel file.xlsx`
          );
          bus.$emit("showToastMessage", {
            message: "File downloaded Successfully! ",
            color: "success",
          });
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showToastMessage", {
            message: "Can't download File ",
            color: "error",
          });
        });
    },
    submitData() {
      // this.loading = true;
      this.serverErrors = [];
      if (this.dataObject && this.dataObject.length > 0) {
        let payload = this.dataObject.filter(
          (item) => Object.keys(item).length > 0
        );
        bus.$emit("showLoader", true);
        this.$api.bulkUpload[this.returnDownloadFunction("upload")](payload)
          .then(() => {
            this.$emit("refreshList");
            bus.$emit("showLoader", false);
            this.loading = false;
            bus.$emit("showToastMessage", {
              message: "Successfully Uploaded File ",
              color: "success",
            });
            this.serverErrors = [];
            this.closeDialog();
          })
          .catch(async (err) => {
            console.error(err.data);
            bus.$emit("showLoader", false);
            if (err.data && !err.data.non_field_errors) {
              this.serverErrors = err.data;
              this.generateErrorRow(err.data);
            } else {
              this.nonFieldErrors = err.data.non_field_errors;
            }
            bus.$emit("showToastMessage", {
              message: "Error Uploading Sheet",
              color: "error",
            });
            this.loading = false;
          });
      } else {
        bus.$emit("showToastMessage", {
          message: "Data not found",
          color: "error",
        });
      }
    },
    generateErrorRow(errorRow) {
      let errorIndexRow = errorRow.map((item) => {
        return Object.keys(item).length > 0 ? true : false;
      });

      this.columnDefs = Object.keys(this.dataObject[0]).map((item) => {
        return {
          headerName: item.replace(/_/g, " ").toUpperCase(),
          field: item,
          editable: "oldIndex" != item ? true : false,
          hide: "oldIndex" == item || "formError" == item ? true : false,
          valueParser: this.dataChange,
          cellStyle: this.cellStyle,
        };
      });

      this.columnDefs.unshift({
        headerName: "Errors",
        pinned: "left",
        width: 90,
        cellRenderer: "BulkUploadErrorTooltip",
      });

      this.columnDefs.push({
        headerName: "Action",
        pinned: "right",
        width: 90,
        cellRenderer: "RemoveRowButtonBulkUpload",
      });
      this.serverErrors = this.serverErrors.filter((item, index) => {
        if (errorIndexRow[index]) {
          return item;
        }
      });

      this.rowData = this.dataObject.filter((item, index) => {
        if (errorIndexRow[index]) {
          return item;
        }
      });
      this.rowData = this.rowData.map((row, index) => {
        let error = this.serverErrors[index];
        return { ...row, formError: error };
      });
    },
    closeDialog() {
      this.bulkUploadDialog = false;
      this.clearDialogData();
    },
    clearDialogData() {
      this.file = null;
      this.fulldata = [];
      this.nonFieldErrors = [];
      this.rowData = [];
      this.columnDefs = [];
      this.dataObject = [];
      this._reqFields = [];
      this.serverErrors = [];
    },
  },
};
</script>

<style>
.drop-box {
  border: 2px dashed var(--v-primary-lighten3);
  height: calc(100vh - 600px);
}
</style>
